import { inject, Injectable } from '@angular/core';
import {
  NavigationCancel,
  NavigationCancellationCode,
  NavigationEnd,
  NavigationStart,
  ResolveEnd,
  ResolveStart,
  Router,
} from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, merge } from 'rxjs';
import { filter, map, skip } from 'rxjs/operators';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class RouterService {
  loading$: Observable<boolean>;
  private showLoading$: Observable<boolean>;
  private hideLoading$: Observable<boolean>;

  private navigationTarget: string | undefined;
  private blockedNavigationTarget: string | undefined;
  private lastSuccessfulRoute: string | undefined;
  private pendingPreviousRoute: string | undefined;

  private router = inject(Router);

  constructor() {
    this.router.events
      .pipe(
        filter(
          (event) =>
            event instanceof NavigationStart &&
            (this.pendingPreviousRoute === undefined ||
              event.url !== this.navigationTarget),
        ),
        skip(1), //skip initial loading/router navigation from / to url
        untilDestroyed(this),
      )
      .subscribe((event: NavigationStart) => {
        const previousUrl = this.router.lastSuccessfulNavigation()?.finalUrl;
        this.pendingPreviousRoute = previousUrl
          ? previousUrl.toString()
          : undefined;
        this.navigationTarget = event.url;
      });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        untilDestroyed(this),
      )
      .subscribe(() => {
        if (this.pendingPreviousRoute !== undefined) {
          this.lastSuccessfulRoute = this.pendingPreviousRoute;
        }
        this.pendingPreviousRoute = undefined;
        this.navigationTarget = undefined;
        this.blockedNavigationTarget = undefined;
      });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationCancel),
        untilDestroyed(this),
      )
      .subscribe((event: NavigationCancel) => {
        this.pendingPreviousRoute = undefined;
        if (event.code === NavigationCancellationCode.GuardRejected) {
          this.blockedNavigationTarget = event.url;
        }
      });

    this.showLoading$ = this.router.events.pipe(
      filter((event) => event instanceof ResolveStart),
      map(() => true)
    );
    this.hideLoading$ = this.router.events.pipe(
      filter((event) => event instanceof ResolveEnd),
      map(() => false)
    );
    this.loading$ = merge(this.showLoading$, this.hideLoading$);
  }

  /**
   * Resumes the navigation a guard rejected most recently, if any.
   * @returns {boolean} Whether a blocked navigation was resumed.
   */
  public continueBlockedNavigation(): boolean {
    if (!this.blockedNavigationTarget) {
      return false;
    }
    void this.router.navigateByUrl(this.blockedNavigationTarget);
    return true;
  }

  /**
   * Navigates to the guard-rejected target, else back to the previous route, else the fallback.
   * @param {string} fallbackURL - URL used when no blocked or previous route exists.
   */
  public continueNavigationTarget(fallbackURL: string = '/dashboard') {
    if (this.continueBlockedNavigation()) {
      return;
    }
    void this.router.navigateByUrl(this.lastSuccessfulRoute ?? fallbackURL);
  }
}
